import React, { useEffect, useState } from "react";
import { router } from "@inertiajs/react";
import Button from "@/components/buttons/Button";
import Select, { SelectOption } from "@/components/inputs/Select";
import FormActions from "@/components/buttons/FormActions";

interface ContractBundleModalProps {
  isOpen: boolean;
  onClose: () => void;
  trackId: number;
  bundleKindOptions: SelectOption[];
  disabled?: boolean;
}

const ContractBundleModal: React.FC<ContractBundleModalProps> = ({
  isOpen,
  onClose,
  trackId,
  bundleKindOptions,
  disabled = false,
}) => {
  const [bundleKind, setBundleKind] = useState<string>("");
  const [processing, setProcessing] = useState<boolean>(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCreate = () => {
    router.post(
      `/tracks/${trackId}/contracts`,
      { bundle_kind: bundleKind },
      {
        onStart: () => setProcessing(true),
        onSuccess: () => onClose(),
        onFinish: () => setProcessing(false),
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleOverlayClick}
    >
      <div className="dark:bg-cream-900 mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="font-sans text-xl font-bold">New contract</h2>
        <p className="text-cream-700 dark:text-cream-400 mt-2">
          Choose the type of contract bundle to generate for this track.
        </p>
        <div className="mt-6">
          <Select
            label="Bundle type"
            name="bundle_kind"
            placeholder="Select a bundle type"
            options={bundleKindOptions}
            value={bundleKind}
            onChange={(e) => setBundleKind(e.target.value)}
          />
        </div>
        <FormActions className="mt-6">
          <Button
            text="Cancel"
            type="secondary"
            htmlButtonType="button"
            onClick={onClose}
            disabled={processing}
          />
          <Button
            text={processing ? "Creating..." : "Create"}
            type="primary"
            htmlButtonType="button"
            onClick={handleCreate}
            disabled={disabled || processing || bundleKind === ""}
          />
        </FormActions>
      </div>
    </div>
  );
};

export default ContractBundleModal;
